import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useWebSocket } from '../../shared/hooks/useWebSocket.js';
import { checkpointApi } from '../../shared/api/api.js';
import { useAuth } from '../../shared/hooks/useAuth.jsx';

const CheckpointContext = createContext(null);

export function CheckpointProvider({ children }) {
  const { user } = useAuth();
  const [cp, setCp] = useState(null);
  const onCpCallbacks  = useRef([]);
  const onClrCallbacks = useRef([]);

  const isParticipant = user?.role === 'user';

  const poll = useCallback(async () => {
    if (!isParticipant) return;
    try {
      const active = await checkpointApi.getActive();
      if (active) {
        const key = `cp_submitted_${active.id}`;
        if (!sessionStorage.getItem(key)) setCp(active);
      } else {
        setCp(null);
      }
    } catch { /* ignore */ }
  }, [isParticipant]);

  const handleMessage = useCallback((msg) => {
    if (msg.type === 'checkpoint_start') {
      const key = `cp_submitted_${msg.data.id}`;
      if (!sessionStorage.getItem(key)) setCp(msg.data);
    }
    if (msg.type === 'checkpoint_end') setCp(null);
  }, []);

  useWebSocket('/ws/live', handleMessage, { enabled: isParticipant, onReconnect: poll });

  // Initial poll
  useEffect(() => { poll(); }, [poll]);

  // 10 másodperces poll fallback
  useEffect(() => {
    if (!isParticipant) return;
    const id = setInterval(poll, 10000);
    return () => clearInterval(id);
  }, [isParticipant, poll]);

  // Poll on visibility/focus
  useEffect(() => {
    if (!isParticipant) return;
    const onVisible = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', poll);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', poll);
    };
  }, [isParticipant, poll]);

  function markSubmitted(id) {
    sessionStorage.setItem(`cp_submitted_${id}`, '1');
    window.dispatchEvent(new CustomEvent('checkpoint-submitted', { detail: { id } }));
    setTimeout(() => setCp(null), 5000);
  }

  return (
    <CheckpointContext.Provider value={{ cp, setCp, markSubmitted }}>
      {children}
    </CheckpointContext.Provider>
  );
}

export function useCheckpoint() {
  return useContext(CheckpointContext);
}
